import { Helmet } from 'react-helmet-async';

const SEO_DATA = {
  home: {
    title: 'WordZingo — Free Word Games & Puzzle Solvers Hub',
    desc:  'Your ultimate word game destination. Play Wordle, Hangman, Word Scramble, Spelling Bee and solve Jumble, Scrabble, Crossword, Anagram puzzles — all free, no sign-up!',
  },
  jumble: {
    title: 'Jumble Solver — Unscramble Any Letters Instantly | WordZingo',
    desc:  'Solve any jumble puzzle in seconds. Enter scrambled letters and find all valid words instantly. Free jumble solver with a 190,000+ word Scrabble dictionary.',
  },
  unscramble: {
    title: 'Word Unscrambler — Find All Words from Letters | WordZingo',
    desc:  'Unscramble any word instantly. Enter your letters and find every possible word, grouped by length. Choose TWL, SOWPODS, or Words With Friends dictionaries. Perfect for Scrabble and daily word puzzles.',
  },
  'anagram-solver': {
    title: 'Anagram Solver - Solve Anagram & Find All Possible Words',
    desc:  'Free anagram solver. Enter your letters — blank tiles (?) supported — and instantly find every word you can make, grouped by length with Scrabble scores. Choose TWL, SOWPODS or Words With Friends dictionaries and click any word for its meaning.',
    app:   true,
    appName: 'WordZingo Anagram Solver',
    faq: [
      { q: 'What is an anagram solver?',
        a: 'An anagram solver rearranges the letters you enter and finds every valid word you can make from them — from full-length anagrams down to shorter words — checked against a real dictionary of 190,000+ words. Results are grouped by length and shown with their Scrabble score.' },
      { q: 'Can I use blank or wildcard tiles?',
        a: 'Yes. Type a question mark (?) for each blank tile — up to two, just like Scrabble and Words With Friends. Each ? can stand in for any letter, so "ca?" finds CAB, CAP, CAT, CAR and more.' },
      { q: 'Does the anagram solver work for Scrabble and Words With Friends?',
        a: 'Yes. Pick the TWL (North American), SOWPODS (international Collins), Words With Friends, or All Dictionaries option and every result is valid in that game. Each word shows its point value so you can spot the best play.' },
      { q: 'What is the difference between an anagram solver and a word unscrambler?',
        a: 'They do the same core job — both find every word you can make from a set of letters. "Anagram solver" is just the more common search term. If you want only exact, full-length rearrangements (same letters, nothing shorter), use our Anagram Generator instead.' },
      { q: 'How many words can I make from my letters?',
        a: 'It depends on your letters. Common letters and vowel-rich combinations (like the tiles in RETINAS or LISTEN) can make dozens of words, while awkward tiles make far fewer. The solver shows a running total and breaks results down by word length.' },
      { q: 'Is using an anagram solver cheating?',
        a: 'It is a learning and practice tool. Many players use it after a game to review missed words, to settle whether a play is valid, or to train their pattern recognition. Whether to use it mid-game is up to you and the people you play with.' },
    ],
  },
  'anagram-generator': {
    title: 'Anagram Generator — Make Name & Word Anagrams Free | WordZingo',
    desc:  'Free anagram generator, anagram maker & funny anagram generator. Turn any word, name, or letters into creative anagrams — including clever multi-word phrases. Switch on Funny mode for the silliest results, anagram your name, and score every word across TWL, SOWPODS & Words With Friends dictionaries.',
    app:   true,
    appName: 'WordZingo Anagram Generator',
    faq: [
      { q: 'What is an anagram generator?',
        a: 'An anagram generator takes a word, name, or set of letters and rearranges them into new words and phrases that use exactly the same letters. Ours finds both single-word anagrams and clever multi-word anagrams, and scores each result.' },
      { q: 'How do I make an anagram of my name?',
        a: 'Type your name into the box (spaces are fine), keep "Include multi-word anagrams" ticked, and generate. The tool rearranges all the letters of your name into phrases — for example CLINT EASTWOOD becomes DISLOCATE TOWN.' },
      { q: 'Can it create multi-word anagrams and phrases?',
        a: 'Yes. Multi-word mode rearranges every letter into readable two- and three-word phrases, which is what makes name anagrams work. Turn it off if you only want exact single-word anagrams.' },
      { q: 'Can it make funny anagrams?',
        a: 'Yes — switch the Anagram Style to "Funny" and the generator rearranges every letter into the silliest, most sentence-like phrases, ranking the funniest results first. It is a great way to get a laugh out of your own name.' },
      { q: 'What is the difference between the Anagram Generator and the Anagram Solver?',
        a: 'The Generator creates true anagrams that use all your letters, including multi-word phrases — ideal for names and creative wordplay. The Anagram Solver finds every word you can make from your letters, including shorter sub-words. Use whichever fits your goal.' },
      { q: 'Is the anagram generator free?',
        a: 'Completely free, with no sign-up and no limits. Generate as many anagrams as you like from any word, name, or letters.' },
      { q: 'How can I get more or better anagrams?',
        a: 'Try switching the dictionary (SOWPODS and All Dictionaries return more options than TWL), make sure multi-word mode is on for names, and experiment with adding a middle name or initials for extra letters to rearrange.' },
    ],
  },
  crossword: {
    title: 'Crossword Solver - Find Crossword Clue & Answers Instantly',
    desc:  'Stuck on a crossword clue? Enter the answer length and any letters you know to instantly find every matching crossword answer. Free crossword clue solver — click any answer for its meaning.',
    app:   true,
    appName: 'WordZingo Crossword Solver',
    faq: [
      { q: 'How do I find a crossword answer with this solver?',
        a: 'Pick the number of letters in the answer, then type any letters you already have from crossing clues and leave the unknown squares blank. The solver instantly lists every word that fits your crossword clue — click any answer to read its meaning.' },
      { q: 'Can the crossword solver read a clue description?',
        a: 'It solves by answer length and known letters, not by interpreting the clue text. Once you know the length and a couple of crossing letters, the pattern matcher narrows thousands of possible crossword answers down to a short, workable list.' },
      { q: 'What if my crossword clue has too many possible answers?',
        a: 'Add more known letters from crossing answers. Even one extra confirmed letter usually cuts the list of possible crossword answers in half, and two or three will often leave just one match.' },
      { q: 'Does it include abbreviations and proper nouns?',
        a: 'The dictionary focuses on common English words used in most crossword wordlists and excludes the majority of proper nouns. If a clue clearly points to a name or place, combine the pattern results with your own general knowledge.' },
      { q: 'Is the crossword clue solver free?',
        a: 'Yes — WordZingo\'s crossword clue solver is completely free, works on phone and desktop, needs no sign-up, and returns unlimited crossword answers.' },
    ],
  },
  'wordle-solver': {
    title: 'Wordle Solver — Get Smart Hints for Today\'s Wordle | WordZingo',
    desc:  'Solve today\'s Wordle fast. Mark your letters green, yellow, or gray and instantly see every word that still fits — a smart Wordle helper with the full 5-letter word list.',
  },
  boggle: {
    title: 'Boggle Solver — 3×3, 4×4, 5×5, 6×6 Grids | WordZingo',
    desc:  'Free Boggle Solver for 3×3, 4×4, 5×5, and 6×6 boards. Type in your letters and instantly see every word you can make — from the shortest to the longest, so you never miss a point.',
  },
  waffle: {
    title: 'Waffle Word Game — Free Online Word Swap Puzzle | WordZingo',
    desc:  'Play the Waffle Word Game free online! Swap letter tiles to spell 6 interlocking words in a waffle grid within 15 swaps. Green locks in, yellow is close. New puzzle every game!',
  },
  scrabble: {
    title: 'Scrabble Word Finder — Highest Scoring Words | WordZingo',
    desc:  'Find the highest-scoring Scrabble words from your tile rack. Supports blank wildcard tiles (?) and TWL, SOWPODS & Words With Friends dictionaries. Results sorted by score. Free Scrabble word finder.',
  },
  'word-ladder': {
    title: 'Word Ladder Solver — Transform Words Step by Step | WordZingo',
    desc:  'Find the shortest word ladder between any two words, changing one letter at a time. See every step from start word to end word instantly — free word ladder solver.',
  },
  rhyme: {
    title: 'Rhyme Finder — Find Perfect Rhymes for Any Word | WordZingo',
    desc:  'Find words that rhyme with any word instantly. Perfect for poetry, songwriting, rap, and creative writing. Rhyme dictionary with 190,000+ words.',
  },
  palindrome: {
    title: 'Palindrome Checker — Is Your Word a Palindrome? | WordZingo',
    desc:  'Check if any word or phrase is a palindrome instantly. Discover palindrome words, phrases, and fun examples. Free palindrome detector.',
  },
  sudoku: {
    title: 'Sudoku Solver — Solve Any Sudoku Puzzle Instantly | WordZingo',
    desc:  'Solve any Sudoku puzzle instantly. Enter the numbers you already have, click Solve, and get the complete, correct solution in a split second — free online Sudoku solver.',
  },
  wordle: {
    title: 'Play Wordle Free — Guess the 5-Letter Word | WordZingo',
    desc:  'Play free Wordle online! Guess the hidden 5-letter word in 6 tries. Green, yellow, and gray tiles guide your guesses. New word every game!',
  },
  hangman: {
    title: 'Play Hangman Free — Classic Word Guessing Game | WordZingo',
    desc:  'Play free Hangman online! Guess the hidden word one letter at a time before the hangman is complete. Classic word game for all ages.',
  },
  'word-search': {
    title: 'Word Search Puzzle — Find Hidden Words Free | WordZingo',
    desc:  'Play free word search puzzles online! Find hidden words in a 14×14 letter grid. Words hide horizontally, vertically, and diagonally. New puzzle every time!',
  },
  'spelling-bee': {
    title: 'Spelling Bee Solver — Find All Answers & Pangrams | WordZingo',
    desc:  'Solve the NYT Spelling Bee puzzle instantly. Enter 7 letters and a center letter — find all valid words and pangrams. Free Spelling Bee solver.',
  },
  'longest-word': {
    title: 'Longest Word Finder — Find the Longest Possible Word | WordZingo',
    desc:  'Find the longest English word from any set of letters. Perfect for Countdown, Scrabble, and word challenges. Instant results from 190,000+ words.',
  },
  'word-pattern': {
    title: 'Word Pattern Finder — Match ?IGHT, C?T Patterns | WordZingo',
    desc:  'Find words matching any letter pattern. Use ? for unknown letters — e.g. ?IGHT finds LIGHT, NIGHT, MIGHT. Perfect for crosswords and Wordle.',
  },
  'scrabble-calculator': {
    title: 'Scrabble Score Calculator — Calculate Any Word Score | WordZingo',
    desc:  'Calculate the exact Scrabble score for any word. Supports double letter, triple letter, double word, and triple word bonus squares. Instant scoring.',
  },
  'pig-latin': {
    title: 'Pig Latin Converter — English ↔ Pig Latin | WordZingo',
    desc:  'Convert any English text to Pig Latin instantly — or translate Pig Latin back to English. Fun language game for kids and adults. Free online converter.',
  },
  'word-scramble': {
    title: 'Word Scramble Game — Race the Clock Free | WordZingo',
    desc:  'Play free Word Scramble online! Unscramble random words before 60 seconds run out. Score points, build streaks, and beat your personal record!',
  },
  'word-chain': {
    title: 'Word Chain Game — Build the Longest Word Chain | WordZingo',
    desc:  'Play free Word Chain! Each word must start with the last letter of the previous. How long can you keep the chain going? Validated against 72,000 words.',
  },
  'syllable-counter': {
    title: 'Syllable Counter — Count Syllables in Any Word | WordZingo',
    desc:  'Count syllables in any word or sentence instantly. Free online syllable counter for poetry, haiku, songwriting, and linguistics. Includes per-word breakdown.',
  },
  'word-counter': {
    title: 'Word Counter — Count Words, Characters & Sentences | WordZingo',
    desc:  'Free online word counter. Instantly count words, characters, sentences, paragraphs, reading time and speaking time. Includes a social media post fit meter for Twitter/X, Instagram, TikTok, LinkedIn & more, plus keyword frequency.',
  },
  cryptogram: {
    title: 'Cryptogram Solver - Solve Cryptoquip & Cryptoquote Puzzles',
    desc:  'Free Cryptogram, Cryptoquip & Cryptoquote solver. Swap coded letters for real ones and watch the hidden quote decode as you type, with letter-frequency hints and sample puzzles.',
  },
  'morse-code': {
    title: 'Morse Code Converter — Text to Morse & Back | WordZingo',
    desc:  'Convert English text to Morse code or decode Morse to text instantly. Play audio beeps, view the full alphabet chart, and copy results. Free Morse code tool.',
  },
  dordle: {
    title: 'Play Dordle Free — Solve 2 Wordle Boards at Once | WordZingo',
    desc:  'Play free Dordle! Guess 2 five-letter words simultaneously in 7 tries. Each guess applies to both boards. How fast can you solve the double Wordle?',
  },
  quordle: {
    title: 'Play Quordle Free — Solve 4 Wordle Boards at Once | WordZingo',
    desc:  'Play free Quordle! Guess 4 five-letter words at the same time in 9 tries. Every guess hits all 4 boards. The ultimate Wordle challenge!',
  },
  connections: {
    title: 'Connections Word Game — Group 16 Words Free | WordZingo',
    desc:  'Play free Connections! Group 16 words into 4 secret categories. Yellow is easiest, purple is fiendish. 10 handcrafted puzzles — how many can you solve?',
  },
  'random-word-generator': {
    title: 'Random Word Generator — Generate English Words Free | WordZingo',
    desc:  'Generate random English words instantly. Filter by word type (noun, verb, adjective, adverb), length, and starting letter. Copy, save favorites, and share your word list — 100% free!',
  },
  'pangram-checker': {
    title: 'Pangram Checker & Generator — Does Your Text Use All 26 Letters? | WordZingo',
    desc:  'Check if any text is a pangram instantly. See which letters are missing, watch the A–Z grid fill up live, generate random pangrams, and browse 20 famous examples. Free pangram tool.',
  },
  about: {
    title: 'About WordZingo — Free Word Games & Puzzle Solvers Hub',
    desc:  'WordZingo is a free word game hub with 37 games and solvers including Wordle, Hangman, Waffle, WordBloom, Letter Boxed, Scrabble Finder, Wordfeud Helper, and more. No sign-up required. Built for word lovers everywhere.',
  },
  'word-twist': {
    title: 'Word Twist — Make Words from 6 Letters Free | WordZingo',
    desc:  'Play free Word Twist online! Unscramble 6 letters into as many words as you can before 2 minutes runs out. Score points for 3, 4, 5 & 6-letter words. The classic text twist word game — free, no download!',
  },
  'text-twist-solver': {
    title: 'Text Twist Solver — Find Every Word & Answer Free | WordZingo',
    desc:  'Free Text Twist Solver and cheat. Enter your 6 or 7 letters and instantly see every valid word grouped by length — 3, 4, 5, 6 and 7-letter answers. Find the bonus word and never get stuck again.',
  },
  privacy: {
    title: 'Privacy Policy | WordZingo',
    desc:  'Read the WordZingo privacy policy. Learn what information we collect, how we use cookies, Google Analytics and AdSense advertising, and the choices you have over your data.',
  },
  terms: {
    title: 'Terms of Use | WordZingo',
    desc:  'Read the WordZingo Terms of Use — the simple rules for using our free word games, puzzle solvers, and word tools, including acceptable use, intellectual property, advertising, and liability.',
  },
  wordbloom: {
    title: 'WordBloom — Free Blossom Word Game Online | WordZingo',
    desc:  'Play WordBloom free! Spell words from a flower of 7 petals — every word must use the center letter. Score the golden bonus petal and find pangrams. A beautiful Blossom-style word game.',
  },
  'letter-boxed': {
    title: 'Letter Boxed — Free Online Letter Box Word Game | WordZingo',
    desc:  'Play Letter Boxed free! Connect the 12 letters around the box into words and use every side — consecutive letters must switch sides. A new solvable puzzle every game, with a 2-word solution.',
  },
  'wordfeud-helper': {
    title: 'Wordfeud Helper — Free Word Finder & Cheat for Wordfeud | WordZingo',
    desc:  'Free Wordfeud Helper and word finder. Enter your rack tiles (blanks supported) and instantly see every valid word ranked by Wordfeud\'s own point values. Beat any board and never miss a high-scoring play.',
  },
};

const SITE = 'WordZingo';
const BASE_URL = 'https://wordzingo.com';

export default function PageSEO({ page }) {
  const data = SEO_DATA[page] || SEO_DATA.home;
  const url = `${BASE_URL}${page === 'home' ? '/' : `/${page}`}`;

  // Optional structured data (JSON-LD) for rich results and stronger relevance
  // signals. Pages opt in via `app` / `faq` fields in SEO_DATA.
  const jsonld = [];
  if (data.app) {
    jsonld.push({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: data.appName || data.title.split('—')[0].trim(),
      url,
      description: data.desc,
      applicationCategory: 'GameApplication',
      operatingSystem: 'Any',
      browserRequirements: 'Requires JavaScript',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      publisher: { '@type': 'Organization', name: SITE, url: BASE_URL },
    });
  }
  if (data.faq) {
    jsonld.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: data.faq.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  return (
    <Helmet>
      <title>{data.title}</title>
      <meta name="description" content={data.desc} />
      <meta property="og:title" content={data.title} />
      <meta property="og:description" content={data.desc} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE} />
      <meta property="og:url" content={url} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={data.title} />
      <meta name="twitter:description" content={data.desc} />
      <link rel="canonical" href={url} />
      {jsonld.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
}
